import JSZip from "jszip";
import { ModelLoadSource, ModelSourceDescriptor, ModelSourceType, SUPPORTED_MODEL_SOURCE_TYPES } from "../../utils/general.utils";
import { getMediaFetchOptions } from "../../utils/media-cache-policy.utils";

export interface ResolvedAssetFile {
  path: string;
  name: string;
  extension: string | null;
  origin: "remote" | "zip";
  url?: string;
  zipFile?: JSZip.JSZipObject;
}

export interface ModelPackage {
  sourceKind: "single" | "archive";
  rootType: ModelSourceType;
  entryFile: ResolvedAssetFile;
  entryUrl: string | null;
  preferredMaterialFile: string | null;
  filesByPath: Map<string, ResolvedAssetFile>;
  filesByName: Map<string, ResolvedAssetFile[]>;
  getFile(pathOrName: string): ResolvedAssetFile | null;
  getFileUrl(pathOrName: string): string | null;
  findFilesByExtension(...extensions: string[]): ResolvedAssetFile[];
  readText(pathOrName: string): Promise<string | null>;
  readArrayBuffer(pathOrName: string): Promise<ArrayBuffer | null>;
  resolveAssetUrl(requestedPath: string): string | null;
  dispose(): void;
}

const SUPPORTED_MODEL_ENTRY_EXTENSIONS = new Set([
  "glb",
  "gltf",
  "fbx",
  "obj",
  "dae",
  "ply",
  "stl",
  "3ds",
]);
const SUPPORTED_MODEL_SOURCE_TYPE_SET = new Set<string>(SUPPORTED_MODEL_SOURCE_TYPES);

const ZIP_VIRTUAL_ROOT = "model-package:///";

function isModelSourceDescriptor(source: ModelLoadSource): source is ModelSourceDescriptor {
  return typeof source !== "string";
}

function isAbsoluteUrl(value: string) {
  return /^(?:[a-z]+:)?\/\//i.test(value) || value.startsWith("blob:") || value.startsWith("data:");
}

function safeDecodeURIComponent(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function stripQueryAndHash(value: string) {
  return String(value || "").split("#")[0].split("?")[0];
}

function normalizeAssetPath(value: string) {
  const cleanedValue = safeDecodeURIComponent(stripQueryAndHash(value)).replace(/\\/g, "/");
  const pathWithoutPrefix = cleanedValue.startsWith(ZIP_VIRTUAL_ROOT)
    ? cleanedValue.slice(ZIP_VIRTUAL_ROOT.length)
    : cleanedValue;
  const rawParts = pathWithoutPrefix.split("/");
  const parts: string[] = [];

  for (const part of rawParts) {
    if (!part || part === ".") continue;
    if (part === "..") {
      parts.pop();
      continue;
    }
    parts.push(part);
  }

  return parts.join("/");
}

function getBasename(value: string) {
  const normalized = normalizeAssetPath(value);
  const parts = normalized.split("/");
  return parts[parts.length - 1] || normalized;
}

function getDirname(value: string) {
  const normalized = normalizeAssetPath(value);
  const parts = normalized.split("/");
  parts.pop();
  return parts.join("/");
}

function getFileExtension(value: string) {
  const name = getBasename(value);
  const match = name.match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1].toLowerCase() : null;
}

function toVirtualPath(value: string) {
  if (!value) return ZIP_VIRTUAL_ROOT;
  if (value.startsWith(ZIP_VIRTUAL_ROOT)) return value;
  return `${ZIP_VIRTUAL_ROOT}${normalizeAssetPath(value)}`;
}

function createResolvedRemoteFile(path: string, url: string): ResolvedAssetFile {
  const normalizedPath = normalizeAssetPath(path);
  return {
    path: normalizedPath,
    name: getBasename(normalizedPath),
    extension: getFileExtension(normalizedPath),
    origin: "remote",
    url,
  };
}

function createResolvedZipFile(file: JSZip.JSZipObject): ResolvedAssetFile {
  const normalizedPath = normalizeAssetPath(file.name);
  return {
    path: normalizedPath,
    name: getBasename(normalizedPath),
    extension: getFileExtension(normalizedPath),
    origin: "zip",
    zipFile: file,
  };
}

function derivePackageRootUrl(fileUrl?: string, filePath?: string) {
  if (!fileUrl) return null;

  try {
    const url = new URL(fileUrl);
    const normalizedPath = normalizeAssetPath(filePath || "");
    const encodedPath = normalizedPath
      .split("/")
      .filter(Boolean)
      .map((part) => encodeURIComponent(part))
      .join("/");

    if (encodedPath && url.pathname.endsWith(encodedPath)) {
      url.pathname = url.pathname.slice(0, url.pathname.length - encodedPath.length);
      if (!url.pathname.endsWith("/")) {
        url.pathname = `${url.pathname}/`;
      }
      return url.toString();
    }

    return new URL(".", fileUrl).toString();
  } catch {
    return null;
  }
}

function scoreArchiveCandidate(file: ResolvedAssetFile, archiveRoot: string) {
  const extension = file.extension || "";
  const priorityOrder = ["glb", "gltf", "fbx", "obj", "dae", "ply", "stl", "3ds"];
  const priorityIndex = priorityOrder.indexOf(extension);
  const depth = file.path.split("/").length;
  const archiveRootName = getBasename(archiveRoot).replace(/\.[^.]+$/, "").toLowerCase();
  const fileStem = file.name.replace(/\.[^.]+$/, "").toLowerCase();
  const rootNameBoost = archiveRootName && archiveRootName === fileStem ? -1 : 0;
  return {
    priorityIndex: priorityIndex >= 0 ? priorityIndex : Number.MAX_SAFE_INTEGER,
    depth,
    rootNameBoost,
    path: file.path,
  };
}

export function detectArchiveEntryFile(
  files: ResolvedAssetFile[],
  archiveRoot: string,
  preferredEntryFile?: string
) {
  if (preferredEntryFile) {
    const normalizedPreferredEntry = normalizeAssetPath(preferredEntryFile);
    const exactMatch = files.find((file) => file.path === normalizedPreferredEntry || file.name === normalizedPreferredEntry);
    if (exactMatch) {
      return exactMatch;
    }
  }

  const candidates = files.filter((file) => file.extension && SUPPORTED_MODEL_ENTRY_EXTENSIONS.has(file.extension));
  if (candidates.length === 0) return null;

  const rankedCandidates = [...candidates].sort((left, right) => {
    const leftScore = scoreArchiveCandidate(left, archiveRoot);
    const rightScore = scoreArchiveCandidate(right, archiveRoot);

    if (leftScore.rootNameBoost !== rightScore.rootNameBoost) {
      return leftScore.rootNameBoost - rightScore.rootNameBoost;
    }

    if (leftScore.depth !== rightScore.depth) {
      return leftScore.depth - rightScore.depth;
    }

    if (leftScore.priorityIndex !== rightScore.priorityIndex) {
      return leftScore.priorityIndex - rightScore.priorityIndex;
    }

    return leftScore.path.localeCompare(rightScore.path);
  });

  if (rankedCandidates.length > 1) {
    const [bestCandidate, secondCandidate] = rankedCandidates;
    const bestScore = scoreArchiveCandidate(bestCandidate, archiveRoot);
    const secondScore = scoreArchiveCandidate(secondCandidate, archiveRoot);

    if (
      bestScore.rootNameBoost === secondScore.rootNameBoost &&
      bestScore.depth === secondScore.depth &&
      bestScore.priorityIndex === secondScore.priorityIndex
    ) {
      console.warn("ModelPackage: Archive contains multiple equally ranked model entry files", {
        selected: bestCandidate.path,
        candidates: rankedCandidates.map((candidate) => candidate.path),
      });
    }
  }

  return rankedCandidates[0];
}

class RuntimeModelPackage implements ModelPackage {
  public readonly filesByPath: Map<string, ResolvedAssetFile>;
  public readonly filesByName: Map<string, ResolvedAssetFile[]>;
  public readonly entryUrl: string | null;

  private readonly filesByLowercasePath = new Map<string, ResolvedAssetFile>();
  private readonly filesByLowercaseName = new Map<string, ResolvedAssetFile[]>();
  private readonly objectUrlsByPath = new Map<string, string>();
  private readonly packageRootUrl: string | null;

  constructor(
    public readonly sourceKind: "single" | "archive",
    public readonly rootType: ModelSourceType,
    public readonly entryFile: ResolvedAssetFile,
    files: ResolvedAssetFile[],
    public readonly preferredMaterialFile: string | null,
    packageRootUrl?: string | null,
    objectUrlsByPath?: Map<string, string>
  ) {
    this.filesByPath = new Map();
    this.filesByName = new Map();
    this.packageRootUrl = packageRootUrl || derivePackageRootUrl(entryFile.url, entryFile.path);

    files.forEach((file) => {
      this.filesByPath.set(file.path, file);
      this.filesByLowercasePath.set(file.path.toLowerCase(), file);

      const filesByName = this.filesByName.get(file.name) || [];
      filesByName.push(file);
      this.filesByName.set(file.name, filesByName);

      const lowercaseName = file.name.toLowerCase();
      const lowerFilesByName = this.filesByLowercaseName.get(lowercaseName) || [];
      lowerFilesByName.push(file);
      this.filesByLowercaseName.set(lowercaseName, lowerFilesByName);
    });

    if (objectUrlsByPath) {
      objectUrlsByPath.forEach((url, path) => this.objectUrlsByPath.set(path, url));
    }

    this.entryUrl = this.getFileUrl(entryFile.path);
  }

  public getFile(pathOrName: string) {
    const normalized = normalizeAssetPath(pathOrName);
    if (!normalized) return null;

    const exactPathMatch = this.filesByPath.get(normalized) || this.filesByLowercasePath.get(normalized.toLowerCase());
    if (exactPathMatch) {
      return exactPathMatch;
    }

    const fileName = getBasename(normalized).toLowerCase();
    const fileNameMatches = this.filesByLowercaseName.get(fileName);
    return fileNameMatches?.[0] || null;
  }

  public getFileUrl(pathOrName: string) {
    const file = this.getFile(pathOrName);
    if (!file) return null;
    if (file.origin === "zip") {
      return this.objectUrlsByPath.get(file.path) || null;
    }
    return file.url || null;
  }

  public findFilesByExtension(...extensions: string[]) {
    const extensionSet = new Set(extensions.map((extension) => extension.toLowerCase()));
    return Array.from(this.filesByPath.values()).filter((file) => file.extension && extensionSet.has(file.extension));
  }

  public async readText(pathOrName: string) {
    const file = this.getFile(pathOrName);
    if (!file) return null;

    if (file.origin === "zip" && file.zipFile) {
      return file.zipFile.async("text");
    }

    if (!file.url) return null;
    const response = await fetch(file.url, getMediaFetchOptions(file.url));
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} while reading ${file.url}`);
    }
    return response.text();
  }

  public async readArrayBuffer(pathOrName: string) {
    const file = this.getFile(pathOrName);
    if (!file) return null;

    if (file.origin === "zip" && file.zipFile) {
      return file.zipFile.async("arraybuffer");
    }

    if (!file.url) return null;
    const response = await fetch(file.url, getMediaFetchOptions(file.url));
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} while reading ${file.url}`);
    }
    return response.arrayBuffer();
  }

  public resolveAssetUrl(requestedPath: string) {
    if (!requestedPath) return null;

    // DAE files often embed absolute file:/// paths from the authoring machine.
    // Always try to remap those references back into the package before allowing
    // the original absolute URL through.
    const directMatchUrl = this.getFileUrl(requestedPath) || this.getFileUrl(getBasename(requestedPath));
    if (directMatchUrl) {
      return directMatchUrl;
    }

    if (isAbsoluteUrl(requestedPath)) return requestedPath;

    const normalizedRequestedPath = normalizeAssetPath(requestedPath);
    if (!normalizedRequestedPath) return null;

    const mappedFileUrl = this.getFileUrl(normalizedRequestedPath);
    if (mappedFileUrl) {
      return mappedFileUrl;
    }

    if (!this.packageRootUrl) {
      return null;
    }

    try {
      return new URL(normalizedRequestedPath, this.packageRootUrl).toString();
    } catch {
      return null;
    }
  }

  public dispose() {
    this.objectUrlsByPath.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrlsByPath.clear();
  }
}

async function buildArchivePackageFromZip(zip: JSZip, descriptor: ModelSourceDescriptor) {
  const files = Object.values(zip.files)
    .filter((file) => !file.dir)
    .map((file) => createResolvedZipFile(file));

  const entryFile = detectArchiveEntryFile(files, descriptor.url, descriptor.entryFile);
  if (!entryFile || !entryFile.extension) {
    console.warn("ModelPackage: No supported 3D file found in archive", {
      archiveUrl: descriptor.url,
      files: files.map((file) => file.path),
    });
    return null;
  }

  const objectUrlsByPath = new Map<string, string>();
  await Promise.all(
    files.map(async (file) => {
      if (!file.zipFile) return;
      const blob = await file.zipFile.async("blob");
      objectUrlsByPath.set(file.path, URL.createObjectURL(blob));
    })
  );

  return new RuntimeModelPackage(
    "archive",
    entryFile.extension as ModelSourceType,
    entryFile,
    files,
    descriptor.materialFile ? normalizeAssetPath(descriptor.materialFile) : null,
    null,
    objectUrlsByPath
  );
}

async function createArchivePackage(descriptor: ModelSourceDescriptor) {
  const response = await fetch(descriptor.url, getMediaFetchOptions(descriptor.url));
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} while loading archive ${descriptor.url}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);
  return buildArchivePackageFromZip(zip, descriptor);
}

function createSingleFilePackage(descriptor: ModelSourceDescriptor) {
  const entryPath = normalizeAssetPath(descriptor.entryFile || getBasename(descriptor.url));
  const entryFile = createResolvedRemoteFile(entryPath, descriptor.url);
  const files = [entryFile];

  if (descriptor.materialFile) {
    const materialPath = normalizeAssetPath(isAbsoluteUrl(descriptor.materialFile) ? getBasename(descriptor.materialFile) : descriptor.materialFile);
    const materialUrl = isAbsoluteUrl(descriptor.materialFile)
      ? descriptor.materialFile
      : new URL(descriptor.materialFile, descriptor.url).toString();
    files.push(createResolvedRemoteFile(materialPath, materialUrl));
  }

  if (descriptor.textureFolder?.length) {
    descriptor.textureFolder.forEach((texture) => {
      if (!texture?.name || !texture?.url) return;
      files.push(createResolvedRemoteFile(texture.name, texture.url));
    });
  }

  return new RuntimeModelPackage(
    "single",
    descriptor.type,
    entryFile,
    files,
    descriptor.materialFile ? normalizeAssetPath(isAbsoluteUrl(descriptor.materialFile) ? getBasename(descriptor.materialFile) : descriptor.materialFile) : null
  );
}

export async function createModelPackage(source: ModelLoadSource): Promise<ModelPackage | null> {
  const descriptor: ModelSourceDescriptor | null = isModelSourceDescriptor(source)
    ? source
    : (() => {
        const sourceType = getFileExtension(source);
        if (!sourceType || !SUPPORTED_MODEL_SOURCE_TYPE_SET.has(sourceType)) {
          return null;
        }

        return {
          type: sourceType as ModelSourceType,
          url: source,
        };
      })();

  if (!descriptor) {
    return null;
  }

  if (descriptor.type === "zip") {
    return createArchivePackage(descriptor);
  }

  return createSingleFilePackage(descriptor);
}

export const ModelPackageUtils = {
  ZIP_VIRTUAL_ROOT,
  getDirname,
  getBasename,
  normalizeAssetPath,
  toVirtualPath,
};
